import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box, Typography, Button, Spinner, Alert, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type ProductDetail, type UpdateProductInput } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { DepartmentChip, FlagChip } from '../components/ProductChips';
import { primaryFlag } from '../lib/productChips';

/**
 * Product editor — edit detail (Figma "Product editor edit detail"). Six cards:
 * Identity · Codes · Size & measure · Classification · Flags & attributes · Data
 * & review status. Codes show ONLY a masked last-4 (feedback_never_show_upc) and
 * are not editable here (UPC entry is the Missing-UPC queue's job).
 */

const PRIMARY_BTN = { backgroundColor: '#f06500', color: '#fff', '&:hover': { backgroundColor: '#d85b00' } };

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9aa3ab', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: '#374151' }}>{value || '—'}</Typography>
    </Box>
  );
}
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, backgroundColor: '#fff', p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{title}</Typography>
      {children}
    </Box>
  );
}
const yn = (b: boolean) => (b ? 'Yes' : 'No');
function sizeImp(p: ProductDetail) {
  return p.unitSizeImperial && p.uomImperial ? `${p.unitSizeImperial} ${p.uomImperial}` : '—';
}
function sizeMet(p: ProductDetail) {
  return p.unitSizeMetric && p.uomMetric ? `${p.unitSizeMetric} ${p.uomMetric}` : '—';
}
function enrichment(p: ProductDetail) {
  const flag = (ok: boolean, name: string) => `${name} ${ok ? '✓' : '✗'}`;
  return `${flag(p.hasAllProductData, 'Product')} · ${flag(p.hasAllNutrition, 'Nutrition')} · ${flag(p.hasAllImages, 'Images')}`;
}

export default function ProductEditDetail() {
  const { glpProductId = '' } = useParams();
  const caps = useCapabilities();
  const canView = caps.has('product:read');
  const canEdit = caps.has('product:edit');
  const navigate = useNavigate();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const request = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++request.current;
    setEditing(false);
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.getProduct(glpProductId);
      if (sequence === request.current) setProduct(res.product);
    } catch (err) {
      if (sequence === request.current) setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      if (sequence === request.current) setLoading(false);
    }
  }, [glpProductId]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  if (caps.loading || loading) return <Centered><Spinner color="brand" /></Centered>;
  if (!canView) return <Alert severity="error">You don’t have permission to view products.</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!product) return <Alert severity="warning">Product not found.</Alert>;

  const p = product;

  return (
    <Box>
      <Box component="button" onClick={() => navigate('/product-editor')}
        sx={{ border: 'none', background: 'none', cursor: 'pointer', p: 0, mb: 1.5, color: '#15803d', fontWeight: 600, fontSize: '14px' }}>
        ‹ Product editor
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75 }}>{p.itemName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <DepartmentChip department={p.glpDepartment} />
            <FlagChip flag={primaryFlag(p)} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {p.brand || '—'} · <span style={{ fontFamily: 'monospace' }}>{p.glpProductId}</span> · {sizeImp(p)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button ariaLabel="Reload product" buttonText="Reload" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => void load()} />
          <Button ariaLabel="Find variants" buttonText="Find variants ›" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => navigate('/product-editor/variants')} />
          {p.evidenceManaged&&caps.data?.features?.productEvidenceReview&&<Button ariaLabel="Review managed product evidence" buttonText="Review evidence" variant="outlined" onClick={()=>navigate(`/product-evidence/${encodeURIComponent(p.glpProductId)}`)}/>}
          {canEdit && !p.evidenceManaged && (
            <Button ariaLabel="Edit product" buttonText="Edit" variant="contained" size="small" sx={PRIMARY_BTN} onClick={() => setEditing(true)} />
          )}
        </Box>
      </Box>

      <Alert severity={p.missingFields?.length ? 'warning' : 'info'}>{p.missingFields?.length ? `Still needs: ${p.missingFields.join(', ')}.` : 'Required data checks pass.'} {p.enrichmentExhaustedAt ? 'Automated sources exhausted. Manual review is still available.' : ''} Review status and data completion are separate.</Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card title="Identity">
          <Field label="ITEM NAME" value={p.itemName} />
          <Field label="ITEM NAME (ES)" value={p.itemNameEs} />
          <Field label="BRAND" value={p.brand} />
          <Field label="MANUFACTURER" value={p.manufacturer} />
          <Field label="SHORT DESCRIPTION" value={p.descriptionShort} />
          <Field label="LONG DESCRIPTION" value={p.descriptionLong} />
        </Card>

        <Card title="Ingredients & nutrition">
          <Field label="INGREDIENTS" value={p.ingredientsText} />
          <Field label="INGREDIENT SOURCE" value={p.ingredientsSource} />
          <Field label="DESCRIPTION SOURCE" value={p.descriptionSource} />
          <Field label="NUTRITION SOURCE" value={p.nutritionSource} />
          <Typography variant="body2">Saved normalized values per 100 g: Calories {p.nutrition?.nutrients?.ENERC_KCAL ?? '—'} kcal · Protein {p.nutrition?.nutrients?.PROCNT ?? '—'} g · Fat {p.nutrition?.nutrients?.FAT ?? '—'} g · Carbohydrate {p.nutrition?.nutrients?.CHOCDF ?? '—'} g</Typography>
          <Field label="IMAGE SOURCE" value={p.primaryImageSource} />
          <Button ariaLabel="Open image review queue" buttonText="Image review queue" variant="outlined" size="small" onClick={() => navigate('/product-images')} />
          <Button ariaLabel="Open missing barcode queue" buttonText="Missing barcode queue" variant="outlined" size="small" onClick={() => navigate('/missing-upc')} />
        </Card>
        <Card title="Review evidence">
          <Field label="REVIEW STATUS" value={p.reviewCompleted ? 'Reviewed' : 'Review open'} />
          {p.reviewHistory?.length ? p.reviewHistory.map((r, i) => <Box key={i}><Typography variant="caption">{new Date(r.at).toLocaleString()} · {r.by}</Typography><Typography variant="body2">{r.evidence}</Typography></Box>) : <Typography variant="body2">No manual enrichment evidence recorded.</Typography>}
        </Card>
        <Card title="Codes">
          <Field label="UPC" value={p.upcLast4} />
          <Field label="EAN" value={p.eanLast4} />
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Only the last 4 are shown — full codes are never exposed.
          </Typography>
        </Card>

        <Card title="Size & measure">
          <Field label="UNIT SIZE (IMPERIAL)" value={sizeImp(p)} />
          <Field label="UNIT SIZE (METRIC)" value={sizeMet(p)} />
          <Field label="NET WEIGHT" value={p.netWeightMg ? `${p.netWeightMg.toLocaleString()} mg` : null} />
          <Field label="NET COUNT" value={p.netCount} />
        </Card>

        <Card title="Classification">
          <Field label="DEPARTMENT" value={p.glpDepartment} />
          <Field label="CATEGORY" value={p.glpCategory} />
          <Field label="SUBCATEGORY" value={p.glpSubCategory} />
          <Field label="CONCEPT ID" value={p.conceptId} />
          <Field label="PRODUCT TAGS" value={p.productTags?.length ? p.productTags.join(' · ') : undefined} />
        </Card>

        <Card title="Flags & attributes">
          <Field label="SNAP ELIGIBLE" value={yn(p.isSnapEligible)} />
          <Field label="ALCOHOLIC" value={yn(p.isAlcoholic)} />
          <Field label="EDIBLE" value={yn(p.isEdible)} />
          <Field label="SUPPLEMENT" value={yn(p.isSupplement)} />
          <Field label="CURRENCY" value={p.currencyCode} />
        </Card>

        <Card title="Data & review status">
          <Field label="ENRICHMENT" value={enrichment(p)} />
          <Field label="ACTIVE RECALL" value={p.hasActiveRecall ? 'Yes — linked recall' : 'None'} />
          <Field label="VARIANT GROUP" value={p.variantGroupId ?? 'Not grouped'} />
          <Field label="REVIEWED BY" value={p.reviewedBy ? `${p.reviewedBy}${p.reviewedAt ? ` · ${new Date(p.reviewedAt).toLocaleDateString()}` : ''}` : undefined} />
          <Field label="LAST UPDATED" value={new Date(p.updatedAt).toLocaleDateString()} />
        </Card>
      </Box>

      {editing && canEdit && (
        <EditProductDialog product={p} onClose={() => setEditing(false)} onSaved={(updated) => { setProduct({ ...p, ...updated }); setEditing(false); void load(); }} />
      )}
    </Box>
  );
}

function EditProductDialog({
  product, onClose, onSaved,
}: {
  product: ProductDetail;
  onClose: () => void;
  onSaved: (updated: Partial<ProductDetail>) => void;
}) {
  const [itemName, setItemName] = useState(product.itemName);
  const [brand, setBrand] = useState(product.brand ?? '');
  const [manufacturer, setManufacturer] = useState(product.manufacturer ?? '');
  const [descriptionShort, setDescriptionShort] = useState(product.descriptionShort ?? '');
  const [glpDepartment, setGlpDepartment] = useState(product.glpDepartment);
  const [glpCategory, setGlpCategory] = useState(product.glpCategory);
  const [glpSubCategory, setGlpSubCategory] = useState(product.glpSubCategory ?? '');
  const [ingredientsText, setIngredientsText] = useState(product.ingredientsText ?? '');
  const [evidence, setEvidence] = useState('');
  const [markReviewed, setMarkReviewed] = useState(product.reviewCompleted);
  const [editNutrition, setEditNutrition] = useState(false);
  const [panel, setPanel] = useState({ energyKcal: '', proteinG: '', fatG: '', carbohydrateG: '' });
  const panelValid = Object.entries(panel).every(([key, value]) => value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= (key === 'energyKcal' ? 1000 : 100));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const input: UpdateProductInput = {
        expectedVersion: product.expectedVersion,
        reviewEvidence: evidence.trim(),
        ...(ingredientsText !== (product.ingredientsText ?? '') ? { ingredientsText: ingredientsText.trim() || null } : {}),
        ...(markReviewed !== product.reviewCompleted ? { markReviewed } : {}),
        ...(editNutrition ? { nutritionPanel: { energyKcal: Number(panel.energyKcal), proteinG: Number(panel.proteinG), fatG: Number(panel.fatG), carbohydrateG: Number(panel.carbohydrateG) } } : {}),
        itemName: itemName.trim(),
        brand: brand.trim() || null,
        manufacturer: manufacturer.trim() || null,
        descriptionShort: descriptionShort.trim() || null,
        glpDepartment: glpDepartment.trim(),
        glpCategory: glpCategory.trim(),
        glpSubCategory: glpSubCategory.trim() || null,
      };
      const res = await adminApiClient.updateProduct(product.glpProductId, input);
      onSaved(res.product);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth paperBorderRadius={16}>
      <DialogTitle>
        <Typography sx={{ fontWeight: 800, fontSize: '18px' }}>Edit product</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Enter verified product facts and record the label or source. Do not guess ingredients or nutrition. Changes are audited; review completion does not bypass missing-data checks.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Item name" ariaLabel="Item name" value={itemName} onChange={(e) => setItemName(e.target.value)} />
          <TextField label="Brand" ariaLabel="Brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
          <TextField label="Manufacturer" ariaLabel="Manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} />
          <TextField label="Short description" ariaLabel="Short description" multiline minRows={2} value={descriptionShort} onChange={(e) => setDescriptionShort(e.target.value)} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Department" ariaLabel="Department" value={glpDepartment} onChange={(e) => setGlpDepartment(e.target.value)} sx={{ flex: 1, minWidth: 160 }} />
            <TextField label="Category" ariaLabel="Category" value={glpCategory} onChange={(e) => setGlpCategory(e.target.value)} sx={{ flex: 1, minWidth: 160 }} />
          </Box>
          <TextField label="Subcategory" ariaLabel="Subcategory" value={glpSubCategory} onChange={(e) => setGlpSubCategory(e.target.value)} />
          <TextField label="Verified ingredients" ariaLabel="Verified ingredients" multiline minRows={3} value={ingredientsText} onChange={e => setIngredientsText(e.target.value)} />
          <Button ariaLabel="Edit verified nutrition" buttonText={editNutrition ? 'Cancel nutrition replacement' : 'Enter verified nutrition'} variant="outlined" onClick={() => setEditNutrition(v => !v)} />
          {editNutrition && <>
            <Alert severity="info">Enter values per 100 g from a verified source. Do not enter per-serving or per-100 ml values here. This replaces the saved panel with these four values; prior data remains in the audit.</Alert>
            {Object.entries({ energyKcal: 'Calories (kcal per 100 g)', proteinG: 'Protein (g per 100 g)', fatG: 'Fat (g per 100 g)', carbohydrateG: 'Carbohydrate (g per 100 g)' }).map(([key, label]) => <TextField key={key} label={label} ariaLabel={label} value={panel[key as keyof typeof panel]} onChange={e => setPanel(previous => ({ ...previous, [key]: e.target.value }))} />)}
          </>}
          <TextField label="Label / source and review notes" ariaLabel="Label / source and review notes" multiline minRows={2} value={evidence} onChange={e => setEvidence(e.target.value)} />
          <Button ariaLabel="Toggle reviewed status" buttonText={markReviewed ? 'Review marked done ✓' : 'Review remains open'} variant="outlined" onClick={() => setMarkReviewed(v => !v)} />
          {err && <Alert severity="error">{err}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button ariaLabel="Cancel" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={onClose} />
        <Button ariaLabel="Save changes" buttonText={saving ? 'Saving…' : 'Save changes'} variant="contained" size="small" disabled={saving || evidence.trim().length < 5 || (editNutrition && !panelValid) || !product.expectedVersion || !itemName.trim() || !glpDepartment.trim() || !glpCategory.trim()} sx={PRIMARY_BTN} onClick={() => void save()} />
      </DialogActions>
    </Dialog>
  );
}
